<script setup lang="ts">
import { getLinkDatasource } from '@/data/link';
import { getTagDatasource } from '@/data/tag';
import LetButton from '@/letapps-vue/components/LetButton.vue';
import { Tag } from '@/types/tag';
import { DxSelectBox, DxTagBox } from 'devextreme-vue';
import { DxButton } from 'devextreme-vue/button';
import { DxForm, DxLabel, DxSimpleItem } from 'devextreme-vue/form';
import { DxItem, DxToolbar } from 'devextreme-vue/toolbar';
import { confirm } from 'devextreme/ui/dialog';
import { ref, watchEffect } from 'vue';
import { useRouter } from 'vue-router';

const router = useRouter();

const props = defineProps({
  linkId: {
    type: Number,
    required: false,
  },
});

const linkDatasource = getLinkDatasource();
const tagDatasource = getTagDatasource();
tagDatasource.sort([{ selector: 'navn', desc: false }]);

const link = ref();
const linkForm = ref();
const loading = ref(false);

async function validerLink() {
  const validation = linkForm.value.instance.validate();

  if (validation.status === 'pending') {
    validation.complete.then(async (asyncValidationResult: any) => {
      if (asyncValidationResult.isValid) await gemLink();
    });
  } else if (validation.isValid) {
    await gemLink();
  }
}

async function gemLink() {
  loading.value = true;

  const newTags: Tag[] = [];
  link.value.tagIds.forEach((tagId: number) => {
    const existingTag = link.value.tags?.find((t: Tag) => t.tagId === tagId);
    if (existingTag) {
      newTags.push(existingTag);
    } else {
      newTags.push({ tagId: tagId, linkId: link.value.id });
    }
  });
  link.value.tags = newTags;

  try {
    await linkDatasource.save(link.value);
  } catch (e) {
    console.error(e);
  }

  loading.value = false;
  await router.push('/admin/links');
}

async function sletLink() {
  let result = confirm(
    'Er du sikker på, at du vil slette <b>' + link.value.navn + '</b>?',
    'Slet link',
  );
  result.then(async (answer) => {
    if (answer) {
      await linkDatasource.store().remove(link.value.id);
      await router.push('/admin/links');
    }
  });
}

async function fortryd() {
  if (props.linkId && props.linkId > 0)
    link.value = await linkDatasource.store().byKey(props.linkId);
  await router.back();
}

watchEffect(async () => {
  if (props.linkId && props.linkId > 0) {
    link.value = await linkDatasource.store().byKey(props.linkId);
  } else {
    link.value = {};
  }

  if (link.value && link.value.tags) {
    link.value.tagIds = link.value.tags.map((tag: Tag) => tag.tagId);
  } else {
    link.value.tagIds = [];
  }
});

const tagsEditorOptions = {
  dataSource: tagDatasource,
  displayExpr: 'navn',
  valueExpr: 'id',
  showSelectionControls: true,
};

const linkTargetEditorOptions = {
  items: [
    { value: '_self', text: 'I samme vindue' },
    { value: '_blank', text: 'I nyt vindue' },
  ],
  valueExpr: 'value',
  displayExpr: 'text',
};

const dokumentTypeEditorOptions = {
  items: [
    { value: 'Diverse materialer (afkastrapporter, faktaark, produktpræsentationer mv.)' },
    { value: 'Brevskabeloner og blanketter' },
    { value: 'Forsikringsbetingelser' },
    { value: 'Kundebreve' },
  ],
  valueExpr: 'value',
  displayExpr: 'value',
};
</script>

<template>
  <div v-if="link">
    <div class="container">
      <div class="left">
        <DxForm ref="linkForm" :form-data="link" :showValidationSummary="true">
          <DxSimpleItem data-field="navn" :is-required="true" />
          <DxSimpleItem data-field="beskrivelse" :is-required="true" editorType="dxTextArea" />
          <DxSimpleItem data-field="linkUrl" :is-required="true" />
          <DxSimpleItem
            data-field="soegningFaneblad"
            editor-type="dxSelectBox"
            :editor-options="dokumentTypeEditorOptions"
          >
            <DxLabel text="Dokument type" />
          </DxSimpleItem>

          <DxSimpleItem
            data-field="tagIds"
            :is-required="true"
            editorType="dxTagBox"
            :editorOptions="tagsEditorOptions"
          >
            <DxLabel text="Tags" />
          </DxSimpleItem>

          <DxItem
            data-field="linkTarget"
            :is-required="true"
            editorType="dxSelectBox"
            :editor-options="linkTargetEditorOptions"
          >
            <DxLabel text="Åbnes" />
          </DxItem>

          <DxSimpleItem>
            <DxLabel text=" " :showColon="false" />
            <DxToolbar>
              <DxItem location="before">
                <LetButton
                  :loading="loading"
                  text="Gem link"
                  :use-submit-behavior="false"
                  @click="validerLink"
                  type="default"
                />
              </DxItem>
              <DxItem location="before">
                <DxButton text="Fortryd" @click="fortryd" />
              </DxItem>
              <DxItem location="after">
                <DxButton
                  text="Slet link"
                  v-if="linkId && linkId > 0"
                  @click="sletLink"
                  type="danger"
                  styling-mode="outlined"
                />
              </DxItem>
            </DxToolbar>
          </DxSimpleItem>
        </DxForm>
      </div>
    </div>
  </div>
</template>

<style scoped lang="scss">
@import '@/css//variables.letguide.scss';

.container {
  display: flex;
  gap: 20px;
}

.left {
  flex: 1;
  max-width: 800px;
}

.right {
  width: 500px;
  padding-left: 20px;
  background-color: $letpension-sand-light;
}
</style>
